import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { getCID, getBalance, recharge, checkPidKey, performOCR, getTenantById } from '../../services/api';
import './Consultas.css';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth';

// Interpretação dos códigos de erro PidKey
const ERROR_CODE_MEANINGS = {
  "0xC004C008": "Obtenha o ID de confirmação no site.",
  "0xC004C020": "Obtenha o ID de confirmação no site / Contate o suporte da Microsoft via Skype.",
  "Get Web": "Obtenha o ID de confirmação no site.",
  "Call MS Support": "Contate o suporte da Microsoft via Skype.",
  "0xC004C060": "Chave inativa.",
  "0xC004C003": "Chave bloqueada.",
  "0xC004C004": "Chave falsa.",
  "Unsupported": "A chave não é suportada para obter o código de erro pelo site."
};

const Consultas: React.FC = () => {
  const [iid, setIid] = useState<string>('');
  const [cid, setCid] = useState<string>('');
  const [orderId, setOrderId] = useState<string>('');
  const [pidKey, setPidKey] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [saldo, setSaldo] = useState<string>('0.00');
  const [precoConsulta, setPrecoConsulta] = useState<string>('0.00');
  const [loading, setLoading] = useState<boolean>(false);
  const [pidKeyLoading, setPidKeyLoading] = useState<boolean>(false);
  const [ocrLoading, setOcrLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [ocrResult, setOcrResult] = useState<string | null>(null);
  const [pidKeyResult, setPidKeyResult] = useState<any>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [ocrIidResult, setOcrIidResult] = useState<string | null>(null);
  const [tenant, setTenant] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    carregarDadosIniciais();
  }, []);

  const carregarSaldoApenasParaAdmin = async (): Promise<void> => {
    try {
      // Verificar se usuário está autenticado e se é admin
      const user = authService.getUser();
      if (!user) {
        console.error('Usuário não autenticado');
        return;
      }

      // Apenas administradores globais veem o saldo direto do GetCID
      if (user.role === 'admin') {
        const response = await getBalance();
        
        // Se houver erro, mostrar mensagem para o usuário
        if (!response.success) {
          console.error('Erro ao carregar saldo:', response.error);
          setError(response.error || 'Erro ao carregar saldo');
          
          // Se for erro de autenticação, redirecionar para login
          if (response.error?.includes('autenticado') || response.error?.includes('expirada')) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            navigate('/login');
            return;
          }
        }
        
        if (response.success && response.data) {
          // Atualizar o saldo se for fornecido na resposta
          if (response.data.balance) {
            setSaldo(response.data.balance);
          }
          
          // Atualizar o preço da consulta se disponível
          if (response.data.price) {
            setPrecoConsulta(response.data.price);
          }
        }
      } else {
        // Ocultar saldo e preços para usuários não-admin
        setSaldo('***');
        setPrecoConsulta('***');
      }
    } catch (error) {
      console.error('Erro ao carregar saldo:', error);
      setError('Erro ao carregar saldo, por favor tente novamente');
    }
  };

  const carregarDadosIniciais = async (): Promise<void> => {
    try {
      // Verificar se usuário está autenticado
      const user = authService.getUser();
      if (!user) {
        setError('Usuário não autenticado');
        return;
      }

      // Carregar dados conforme o tipo de usuário
      if (user.role === 'client') {
        // Para clientes, buscar dados do tenant pelo ID
        if (user.tenantId) {
          // // console.log('Carregando dados financeiros para o tenant ID:', user.tenantId);
          
          // Buscar os dados do tenant diretamente do banco de dados
          const tenantResponse = await getTenantById(user.tenantId);
          // // console.log('Tenant response:', tenantResponse);
          
          if (tenantResponse.success && tenantResponse.tenant) {
            // Atualizar o tenant completo no estado
            setTenant(tenantResponse.tenant);
            
            // Atualizar o saldo do tenant
            const novoSaldo = tenantResponse.tenant.credits?.toString() || '0.00';
            setSaldo(novoSaldo);
            // // console.log('Saldo atualizado do cliente:', novoSaldo);
            
            // Forçar atualização visual para garantir consistência
            setTimeout(() => {
              document.querySelectorAll('.saldo-value').forEach(el => {
                if (el.textContent?.includes('Créditos')) {
                  el.textContent = `Créditos ${parseFloat(novoSaldo).toFixed(2)}`;
                }
              });
            }, 100);
          }
        }
      } else {
        // Para administradores, mostrar dados do GetCID
        setTenant({ name: 'Administrador', credits: 'N/A' });
        await carregarSaldoApenasParaAdmin();
      }
    } catch (error) {
      setError('Erro ao carregar dados iniciais');
      console.error(error);
    }
  };

  const carregarSaldo = async (): Promise<void> => {
    if (authService.getUser()?.role === 'admin') {
      await carregarSaldoApenasParaAdmin();
    }
  };

  const atualizarSaldoAposOperacao = async (response: any): Promise<void> => {
    const user = authService.getUser();
    const isAdmin = user?.role === 'admin';
    
    // Apenas atualiza o saldo se for um tenant (cliente)
    if (!isAdmin && response && response.credits !== undefined) {
      const creditoAnterior = parseFloat(saldo);
      const novoCredito = response.credits;
      const diferenca = creditoAnterior - novoCredito;
      
      // Atualiza o saldo no estado (que será refletido na parte superior e inferior)
      setSaldo(novoCredito.toString());
      
      // Atualiza também o saldo no tenant
      if (tenant) {
        setTenant({
          ...tenant,
          credits: novoCredito
        });
      }
      
      // Força a atualização do saldo na interface
      document.querySelectorAll('.saldo-value').forEach(el => {
        if (el.textContent?.includes('Créditos')) {
          el.textContent = `Créditos ${parseFloat(novoCredito).toFixed(2)}`;
        }
      });
      
      // Exibir uma mensagem temporária sobre a dedução de créditos
      if (diferenca > 0) {
        setError(`Foram descontados ${diferenca.toFixed(2)} créditos do seu saldo.`);
        setTimeout(() => setError(null), 5000); // Remove a mensagem após 5 segundos
      }
    }
  };

  // Adicionar função para atualizar dados do tenant a partir do backend
  const atualizarDadosTenant = async (): Promise<void> => {
    const user = authService.getUser();
    if (user && user.role === 'client' && user.tenantId) {
      try {
        // Importar a função necessária
        const { getTenantById } = await import('../../services/api');
        const response = await getTenantById(user.tenantId);
        
        if (response.success && response.tenant) {
          // Atualizar o tenant completo
          setTenant(response.tenant);
          // Atualizar o saldo
          setSaldo(response.tenant.credits?.toString() || '0.00');
          
          // console.log('Saldo atualizado do backend:', response.tenant.credits);
        }
      } catch (error) {
        console.error('Erro ao atualizar dados do tenant:', error);
      }
    }
  };

  const handleGetCID = async (): Promise<void> => {
    if (!iid) {
      setError('Por favor, insira um IID');
      return;
    }

    setLoading(true);
    setError(null);
    // Limpar o CID anterior
    setCid('');

    try {
      // Adicionar timestamp para evitar cache
      const timestamp = new Date().getTime();
      console.log(`[${timestamp}] Iniciando consulta para IID: ${iid}`);
      
      const response = await getCID(iid);
      // Log da resposta completa para debug
      // console.log(`[${timestamp}] Resposta completa da consulta CID:`, response);
      
      if (response.success && response.data) {
        // Log dos dados da resposta
        // console.log(`[${timestamp}] Dados da resposta:`, response.data);
        //console.log(`[${timestamp}] Status da resposta:`, response.data.status);
        
        // Verificar se a consulta foi bem-sucedida (status: success)
        if (response.data.status === 'success') {
          // Se for sucesso, mostrar o CID
          setCid(response.data.cid);
          // console.log(`[${timestamp}] CID obtido com sucesso: ${response.data.cid}`);
          
          // Atualizar o saldo com o valor retornado da API apenas se a consulta for bem-sucedida
          await atualizarSaldoAposOperacao(response);
          // Atualizar dados do tenant a partir do backend
          await atualizarDadosTenant();
        } else if (response.data.status === 'failed') {
          // Se falhou, mostrar a mensagem de erro no lugar do CID
          let errorMessage = 'Chave inválida';
          if (response.data.errormsg) {
            // Os erros conhecidos:
            // 0xC004C060: Dead key.
            // 0xC004C003: Key blocked.
            // 0xC004C004: Fake key.
            if (response.data.errormsg.includes('0xC004C060')) {
              errorMessage = 'Chave inoperante (Dead key)';
            } else if (response.data.errormsg.includes('0xC004C003')) {
              errorMessage = 'Chave bloqueada (Key blocked)';
            } else if (response.data.errormsg.includes('0xC004C004')) {
              errorMessage = 'Chave falsificada (Fake key)';
            } else {
              errorMessage = response.data.errormsg;
            }
          }
          setCid(`ERRO: ${errorMessage}`);
          // Também exibir como erro normal
          setError(errorMessage);
          console.log(`[${timestamp}] Erro na consulta: ${errorMessage}`);
          
          // Verificar se houve reembolso de créditos
          if (response.creditsRefunded !== undefined) {
            console.log(`[${timestamp}] Créditos reembolsados: ${response.creditsRefunded}`);
          }
        }
      } else {
        setError(response.error || 'Erro ao consultar CID');
        console.log(`[${timestamp}] Erro na resposta:`, response.error || 'Erro desconhecido');
      }
    } catch (error) {
      setError('Erro ao consultar CID');
      console.error('Erro na consulta:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecharge = async (): Promise<void> => {
    if (!orderId) {
      setError('Por favor, insira um OrderID');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await recharge(orderId);
      if (response.success && response.data) {
        // Atualizar o saldo com o valor retornado da API
        await atualizarSaldoAposOperacao(response.data);
        setError(null);
      } else {
        setError(response.error || 'Erro ao recarregar');
      }
    } catch (error) {
      setError('Erro ao recarregar');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckPidKey = async (): Promise<void> => {
    if (!pidKey) {
      setError('Por favor, insira uma PidKey');
      return;
    }

    setPidKeyLoading(true);
    setError(null);
    setPidKeyResult(null);

    try {
      const response = await checkPidKey(pidKey);
      if (response.success && response.data) {
        setPidKeyResult(response.data);
        // Atualizar o saldo com o valor retornado da API
        await atualizarSaldoAposOperacao(response.data);
        // Atualizar dados do tenant a partir do backend
        await atualizarDadosTenant();
      } else {
        setError(response.error || 'Erro ao verificar PidKey');
      }
    } catch (error) {
      setError('Erro ao verificar PidKey');
    } finally {
      setPidKeyLoading(false);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
      setOcrResult(null);
    }
  };

  const handleOCR = async (): Promise<void> => {
    if (!selectedFile) {
      setError('Por favor, selecione uma imagem');
      return;
    }

    setOcrLoading(true);
    setError(null);
    setOcrResult(null);
    setOcrIidResult(null);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        if (e.target?.result) {
          const base64 = e.target.result.toString().split(',')[1];
          const response = await performOCR(base64);
          if (response.success && response.data) {
            // Extrair apenas o result e status
            const responseData = response.data;
            setOcrResult(`Status: ${responseData.status === 'success' ? 'Sucesso' : 'Falha'}\nResultado: ${responseData.result}`);
            
            // Se houver um resultado e o status for sucesso, armazena o result para usar no modal
            if (responseData.status === 'success' && responseData.result) {
              setOcrIidResult(responseData.result);
              setShowModal(true);
            }
            
            // Atualizar o saldo com o valor retornado da API
            await atualizarSaldoAposOperacao(response.data);
            // Atualizar dados do tenant a partir do backend
            await atualizarDadosTenant();
          } else {
            setError(response.error || 'Erro ao realizar OCR');
          }
          setOcrLoading(false);
        }
      };
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      setError('Erro ao realizar OCR');
      setOcrLoading(false);
    }
  };

  const handleConsultarOcrResultado = (): void => {
    if (ocrIidResult) {
      setIid(ocrIidResult);
      setShowModal(false);
      // Chamar a consulta de CID com o ID obtido do OCR
      setTimeout(() => {
        handleGetCIDFromOCR();
      }, 100);
    }
  };

  // Nova função para consultar CID a partir do OCR sem validação de campo vazio
  const handleGetCIDFromOCR = async (): Promise<void> => {
    if (!ocrIidResult) {
      return;
    }

    setLoading(true);
    setError(null);
    // Limpar o CID anterior
    setCid('');

    try {
      const response = await getCID(ocrIidResult);
      // Log da resposta completa para debug
      // console.log('Resposta completa da consulta CID via OCR:', response);
      
      if (response.success && response.data) {
        // Log dos dados da resposta
        // console.log('Dados da resposta OCR:', response.data);
        // console.log('Status da resposta OCR:', response.data.status);
        
        // Verificar se a consulta foi bem-sucedida (status: success)
        if (response.data.status === 'success') {
          // Se for sucesso, mostrar o CID
          setCid(response.data.cid);
          
          // Atualizar o saldo com o valor retornado da API apenas se a consulta for bem-sucedida
          await atualizarSaldoAposOperacao(response.data);
          // Atualizar dados do tenant a partir do backend
          await atualizarDadosTenant();
        } else if (response.data.status === 'failed') {
          // Se falhou, mostrar a mensagem de erro no lugar do CID
          let errorMessage = 'Chave inválida';
          if (response.data.errormsg) {
            // Os erros conhecidos:
            // 0xC004C060: Dead key.
            // 0xC004C003: Key blocked.
            // 0xC004C004: Fake key.
            if (response.data.errormsg.includes('0xC004C060')) {
              errorMessage = 'Chave inoperante (Dead key)';
            } else if (response.data.errormsg.includes('0xC004C003')) {
              errorMessage = 'Chave bloqueada (Key blocked)';
            } else if (response.data.errormsg.includes('0xC004C004')) {
              errorMessage = 'Chave falsificada (Fake key)';
            } else {
              errorMessage = response.data.errormsg;
            }
          }
          setCid(`ERRO: ${errorMessage}`);
          // Também exibir como erro normal
          setError(errorMessage);
          
          // Não atualizar o saldo quando a consulta falhar
        }
      } else {
        setError(response.error || 'Erro ao consultar CID');
      }
    } catch (error) {
      setError('Erro ao consultar CID');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: FormEvent, callback: () => Promise<void>): void => {
    e.preventDefault();
    callback();
  };

  const getErrorCodeMeaning = (errorCode: string): string => {
    return ERROR_CODE_MEANINGS[errorCode as keyof typeof ERROR_CODE_MEANINGS] || 'Código de erro desconhecido';
  };

  const renderPidKeyResult = () => {
    if (!pidKeyResult) return null;

    return (
      <div className="resultado">
        <div className="pid-key-info">
          <h4>Informações da PidKey</h4>
          <p><strong>Status:</strong> {pidKeyResult.status === 'success' ? 'Sucesso' : 'Falha'}</p>
          <p><strong>Máximo:</strong> {pidKeyResult.max} consultas</p>
          <p><strong>Utilizadas:</strong> {pidKeyResult.used} consultas</p>
          <p><strong>Validade:</strong> {pidKeyResult.end}</p>
          <p><strong>Última verificação:</strong> {pidKeyResult.last}</p>
          <p><strong>Código de erro:</strong> {pidKeyResult.errorcode}</p>

          {pidKeyResult.keydata && pidKeyResult.keydata.length > 0 && (
            <div className="key-details">
              <h4>Detalhes da chave</h4>
              {pidKeyResult.keydata.map((key: any, index: number) => (
                <div key={index} className="key-entry">
                  <p><strong>Produto:</strong> {key.Description}</p>
                  <p><strong>Chave:</strong> {key.ProductKey}</p>
                  <p><strong>Código de erro:</strong> {key.ErrorCode}</p>
                  <p><strong>Significado:</strong> {getErrorCodeMeaning(key.ErrorCode)}</p>
                  <p><strong>Verificada em:</strong> {key.CheckedTime}</p>
                </div>
              ))}
            </div>
          )}

          <div className="error-codes-info">
            <h4>Significado dos Códigos de Erro:</h4>
            <ul>
              {Object.entries(ERROR_CODE_MEANINGS).map(([code, meaning]) => (
                <li key={code}><strong>{code}:</strong> {meaning}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="consultas-container">
      <h2>Consultas</h2>
      
      {/* Adicionar alerta para administradores */}
      {authService.getUser()?.role === 'admin' && (
        <div className="admin-alert">
          <p>⚠️ Você está operando como administrador. As consultas não descontarão créditos do sistema.</p>
        </div>
      )}
      
      {/* Exibir saldo e informações financeiras */}
      <div className="financial-info">
        <div className="saldo-container">
          <span className="saldo-label">Créditos disponíveis:</span>
          <span className="saldo-value">
            {authService.getUser()?.role === 'admin' 
              ? `$ ${parseFloat(saldo).toFixed(2)}` 
              : `${parseFloat(saldo).toFixed(2)}`
            }
          </span>
        </div>
      </div>
      
      <div className="consultas-grid">
        <div className="consulta-card">
          <h2>ID de Instalação</h2>
          <form onSubmit={(e) => handleSubmit(e, handleGetCID)}>
            <input
              type="text"
              value={iid}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setIid(e.target.value)}
              placeholder="Digite o IID"
            />
            <button type="submit" disabled={loading}>
              {loading ? 'Consultando...' : 'Consultar'}
            </button>
            {loading && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <div className="loading-text">Consultando CID...</div>
              </div>
            )}
            {cid && (
              <div className="resultado">
                {cid.startsWith('ERRO:') ? (
                  <p>Resultado: <span className="cid-error">{cid}</span></p>
                ) : (
                  <p>CID: <span className="cid-value">{cid}</span></p>
                )}
              </div>
            )}
          </form>
        </div>

        {/* Mostrar card de recarga apenas para administradores */}
        {authService.getUser()?.role === 'admin' && (
          <div className="consulta-card">
            <h2>Recarregar Créditos</h2>
            <form onSubmit={(e) => handleSubmit(e, handleRecharge)}>
              <input
                type="text"
                value={orderId}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setOrderId(e.target.value)}
                placeholder="Digite o OrderID"
              />
              <button type="submit" disabled={loading}>
                {loading ? 'Recarregando...' : 'Recarregar'}
              </button>
              {loading && (
                <div className="loading-overlay">
                  <div className="loading-spinner"></div>
                  <div className="loading-text">Recarregando créditos...</div>
                </div>
              )}
            </form>
          </div>
        )}

        <div className="consulta-card">
          <h2>Verificar PidKey</h2>
          <form onSubmit={(e) => handleSubmit(e, handleCheckPidKey)}>
            <input
              type="text"
              value={pidKey}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setPidKey(e.target.value)}
              placeholder="Digite a PidKey"
            />
            <button type="submit" disabled={pidKeyLoading}>
              {pidKeyLoading ? 'Verificando...' : 'Verificar'}
            </button>
            {pidKeyLoading && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <div className="loading-text">Verificando PidKey... Isso pode levar algum tempo.</div>
              </div>
            )}
            {pidKeyResult && renderPidKeyResult()}
          </form>
        </div>

        <div className="consulta-card">
          <h2>OCR de Imagem</h2>
          <form onSubmit={(e) => handleSubmit(e, handleOCR)}>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
            <button type="submit" disabled={ocrLoading || !selectedFile}>
              {ocrLoading ? 'Processando...' : 'Realizar OCR'}
            </button>
            {ocrLoading && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <div className="loading-text">Processando imagem... Isso pode levar algum tempo.</div>
              </div>
            )}
            {ocrResult && (
              <div className="resultado">
                <pre>{ocrResult}</pre>
              </div>
            )}
          </form>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      
      {/* Informações de pagamento apenas para administradores - fora da grade de consultas */}
      {authService.getUser()?.role === 'admin' && (
        <div className="payment-options payment-section">
          <h2>Opções de Pagamento</h2>
          <div className="payment-details">
            <p><strong>Binance ID:</strong> (739474182). Após o pagamento, envie o ID do pedido para o Server.</p>
            <p><strong>Paypal:</strong> (bkkmms@outlook.com). Após o pagamento, envie o ID da transação para o Server.</p>
            <p><strong>Crypto USDT TRC20:</strong> TLLWTmymMQTEz2FKAGhVeUjEGh2P9ADHRo. Após o pagamento, envie o txId ou ID de transferência interna para o Server.</p>
            <p><strong>Stripe:</strong> (https://buy.stripe.com/4gwdRM46H7PQ6Qw5km). Após o pagamento, envie o número do recibo para o Server. Você pode obtê-lo em seu e-mail.</p>
            <p><em>Pagamentos via PayPal e Stripe terão taxas deduzidas: 4.4% + $0.30 e 2.9% + $0.30.</em></p>
            
            <h3>Detalhes de Preços:</h3>
            <ul>
              <li>$0.07 por unidade.</li>
              <li>Recarga: $50, preço unitário: $0.065/cid.</li>
              <li>Recarga: $100, preço unitário: $0.06/cid.</li>
              <li>Recarga: $150, preço unitário: $0.055/cid.</li>
            </ul>
          </div>
        </div>
      )}
      
      {/* Modal para consultar o IID obtido do OCR */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>ID de Instalação Detectado</h3>
            <p>Foi detectado o seguinte ID de instalação:</p>
            <div className="modal-iid">{ocrIidResult}</div>
            <p>Deseja consultá-lo agora?</p>
            <div className="modal-buttons">
              <button onClick={handleConsultarOcrResultado} className="modal-button confirm">
                Sim, Consultar CID
              </button>
              <button onClick={() => setShowModal(false)} className="modal-button cancel">
                Não, Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Consultas; 